import { UseTranslateType } from 'module/hooks/use-translation';
import { JSONContent } from '@tiptap/core';
import { GenerateAsOption, GroupByOption } from 'api/custom-section/types';

export interface ConfigurationContentConfig {
  groupBy: GroupByOption;
  generateAs: GenerateAsOption;
}
export const getConfigurationPlaceholderNodes = (
  config: ConfigurationContentConfig,
  translate: UseTranslateType,
): JSONContent[] => {
  const { groupBy, generateAs } = config;

  const topic = translate('Topic');
  const company = translate('Company');
  const participant = translate('Participant');
  const timePeriod = translate('TimePeriod');
  const thisMonth = translate('timeperiod.this_month');
  const action = translate('configuration.action');
  const lastMonth = translate('timeperiod.last_month');

  const getNodesByType = (label: string): JSONContent[] => {
    switch (generateAs) {
      case GenerateAsOption.Paragraph:
        return [
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faAlignLeft',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `${label} 1`,
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
              disabled: true,
            },
            content: [
              {
                type: 'text',
                text: '...',
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faAlignLeft',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `${label} 2`,
              },
            ],
          },
        ];
      case GenerateAsOption.Bullet:
        return [
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faCircle',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `${label} 1`,
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
              disabled: true,
            },
            content: [
              {
                type: 'text',
                text: '...',
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faCircle',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `${label} N`,
              },
            ],
          },
        ];
      case GenerateAsOption.Number:
        return [
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `1. ${label}`,
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
              disabled: true,
            },
            content: [
              {
                type: 'text',
                text: '...',
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `N. ${label}`,
              },
            ],
          },
        ];
      case GenerateAsOption.Checklist:
        return [
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faSquareCheck',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `${action} 1`,
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
              disabled: true,
            },
            content: [
              {
                type: 'text',
                text: '...',
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faSquareCheck',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `${action} N`,
              },
            ],
          },
        ];
      default:
        return [];
    }
  };

  const getNone = (): JSONContent[] => {
    switch (generateAs) {
      case GenerateAsOption.Paragraph:
        return [
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faAlignLeft',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: translate('editor.toolbar.basic_formatting.paragraph'),
              },
            ],
          },
        ];
      case GenerateAsOption.Bullet:
        return [
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faCircle',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `${translate('editor.toolbar.basic_formatting.bullet_list')} 1`,
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
              disabled: true,
            },
            content: [
              {
                type: 'text',
                text: '...',
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faCircle',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `${translate('editor.toolbar.basic_formatting.bullet_list')} N`,
              },
            ],
          },
        ];
      case GenerateAsOption.Number:
        return [
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `1. ${translate('editor.toolbar.basic_formatting.bullet_list')}`,
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
              disabled: true,
            },
            content: [
              {
                type: 'text',
                text: '...',
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `N. ${translate('editor.toolbar.basic_formatting.bullet_list')}`,
              },
            ],
          },
        ];
      case GenerateAsOption.Checklist:
        return [
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faSquareCheck',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `${translate('configuration.action')} 1`,
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
              disabled: true,
            },
            content: [
              {
                type: 'text',
                text: '...',
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faSquareCheck',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: `${translate('configuration.action')} N`,
              },
            ],
          },
        ];
      default:
        return [];
    }
  };

  switch (groupBy) {
    case GroupByOption.Topics:
      return getNodesByType(topic);
    case GroupByOption.Company:
      return getNodesByType(company);
    case GroupByOption.Participants:
      return getNodesByType(participant);
    case GroupByOption.TimePeriod:
      if (generateAs === GenerateAsOption.Paragraph) {
        return [
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faAlignLeft',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: thisMonth,
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
              disabled: true,
            },
            content: [
              {
                type: 'text',
                text: '...',
              },
            ],
          },
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: 'faAlignLeft',
              iconPosition: 'left',
            },
            content: [
              {
                type: 'text',
                text: lastMonth,
              },
            ],
          },
        ];
      }

      return getNodesByType(timePeriod);
    case GroupByOption.None:
      return getNone();
    default:
      return [];
  }
};
